import { Component, computed, input, model, output, signal } from "@angular/core";

import { NzAlertModule } from "ng-zorro-antd/alert";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";
import { NzIconModule } from "ng-zorro-antd/icon";
import { NzListModule } from "ng-zorro-antd/list";
import { NzModalModule } from "ng-zorro-antd/modal";
import { NzTagModule } from "ng-zorro-antd/tag";
import { NzTreeNode } from "ng-zorro-antd/tree";

import { FormatBytesPipe } from "../../../shared/pipes/format-bytes.pipe";
import { TargetStorageClass } from "../../../models/s3-item.model";

/** The modal decides *what* is eligible; addressing those nodes for the API is the
 *  caller's job, since only it knows how the tree's root maps onto a bucket prefix. */
export interface StorageClassRequest {
  target: TargetStorageClass;
  /** Folder nodes. The server expands each prefix; the client cannot enumerate them. */
  folders: NzTreeNode[];
  /** Individually selected files, already filtered down to Standard. */
  files: NzTreeNode[];
}

const TARGETS: Record<
  TargetStorageClass,
  { label: string; okText: string; warning: string }
> = {
  GLACIER_IR: {
    label: "Glacier Instant Retrieval",
    okText: "Move to Glacier IR",
    warning:
      "Objects are billed for a 90-day minimum. Retrieval stays instant, but deleting or re-transitioning early is charged for the full 90 days.",
  },
  DEEP_ARCHIVE: {
    label: "Deep Archive",
    okText: "Move to Deep Archive",
    warning:
      "Objects are billed for a 180-day minimum and cannot be read until restored, which takes up to 12 hours.",
  },
};

/** Only Standard objects are worth transitioning; anything already archived is skipped. */
const ELIGIBLE_STORAGE_CLASS = "STANDARD";

/** Long selections are summarised rather than listed in full — a confirmation is for
 *  judgement, not for auditing every key. */
const MAX_LISTED = 30;

@Component({
  selector: "app-storage-class-confirm",
  imports: [
    NzAlertModule,
    NzCheckboxModule,
    NzIconModule,
    NzListModule,
    NzModalModule,
    NzTagModule,
    FormatBytesPipe,
  ],
  templateUrl: "./storage-class-confirm.component.html",
  styleUrl: "./storage-class-confirm.component.less",
})
export class StorageClassConfirmComponent {
  /** null keeps the modal closed; setting a class opens it for that action. */
  readonly target = model<TargetStorageClass | null>(null);
  /** Straight from NzTreeComponent.getCheckedNodeList(). */
  readonly selection = input<NzTreeNode[]>([]);
  /** The bucket every key below belongs to — stated once rather than repeated per row. */
  readonly bucket = input("");
  readonly confirmed = output<StorageClassRequest>();

  protected readonly maxListed = MAX_LISTED;

  /** One scroll region for the whole body, so the modal never outgrows the viewport.
   *  Held as a field rather than an object literal in the template: [nzBodyStyle] binds
   *  straight to [style], and a fresh object every change detection pass re-applies it. */
  protected readonly bodyStyle = {
    "max-height": "calc(100vh - 220px)",
    "overflow-y": "auto",
  };
  protected acknowledged = signal(false);

  protected copy = computed(() => {
    const target = this.target();
    return target ? TARGETS[target] : null;
  });

  /** Anything that is not a file is a prefix */
  protected folders = computed(() =>
    this.selection().filter((node) => node.origin.type !== "file"),
  );
  private files = computed(() =>
    this.selection().filter((node) => node.origin.type === "file"),
  );

  protected eligibleFiles = computed(() =>
    this.files().filter(
      (file) => file.origin.storage_class !== this.target(),
    ),
  );
  protected skippedFiles = computed(() =>
    this.files().filter(
      (file) => file.origin.storage_class === this.target(),
    ),
  );
  protected eligibleBytes = computed(() =>
    this.eligibleFiles().reduce((sum, file) => sum + (file.origin.size ?? 0), 0),
  );

  protected canConfirm = computed(
    () =>
      (this.folders().length > 0 || this.eligibleFiles().length > 0) &&
      // Deep Archive is the one that cannot be read for hours, so it asks for an explicit ack.
      (this.target() !== "DEEP_ARCHIVE" || this.acknowledged()),
  );

  protected close() {
    this.acknowledged.set(false);
    this.target.set(null);
  }

  protected confirm() {
    const target = this.target();
    if (!target || !this.canConfirm()) return;

    this.confirmed.emit({
      target,
      folders: this.folders(),
      files: this.eligibleFiles(),
    });
    this.close();
  }
}
